<template>
  <div>
    <VDialog
        v-if="isDialogOpen"
        :value="isDialogOpen"
        width="1020"
        persistent
        :fullscreen="$vuetify.breakpoint.xs"
        :contentClass="$vuetify.breakpoint.xs ? 'direction-dialog' : ''"
        outlined
        :scrollable="$vuetify.breakpoint.xs"
    >
      <DirectionDialog
          ref="directionDialog"
          data-test="directionSelectionDialog"
          :openFrom="currentStep"
          :currentStep="currentStep"
          :isLoadingSelectedOpsZone="isLoadingSelectedOpsZone"
          :isLoading="isLoading"
          :opsZoneList="opsZoneList"
          :communeCoordinates="communeCoordinates"
          :isNightCardsSupport="isNightCardsSupport"
          :additionalMapZonesColor="additionalMapZonesColor"
          :opsZoneColor="opsZoneColor"
          :opsZoneMapFill="opsZoneMapFill"
          :opsZoneMapStroke="opsZoneMapStroke"
          :communeMapFill="communeMapFill"
          :communeMapStroke="communeMapStroke"
          :stepsMeta="stepsMeta"
          :getArrivalPlace="getArrivalPlace"
          :getDeparturePlace="getDeparturePlace"
          :getIsArrivalPlaceFilled="getIsArrivalPlaceFilled"
          :getIsDeparturePlaceFilled="getIsDeparturePlaceFilled"
          :poiList="poiList"
          :error="error"
          :poiListWithoutGrouping="poiListWithoutGrouping"
          @changeStep="changeStep"
          @setLocation="setLocation"
          @setError="setError"
          @loadState="loadState"
          @closeDialog="closeDialog"
      />
    </VDialog>
    <ValidationObserver
      ref="directionSelection"
      name="directionSelection"
    >
      <div @click.stop="openPickUpDialog">
        <ValidationProvider
            tag="div"
            v-slot="{ errors, ariaInput }"
            ref="departure"
            name="departure"
            mode="lazy"
            :rules="{ required: true }"
        >
          <VTextField
              v-bind="ariaInput"
              readonly
              data-test="directionFieldFrom"
              :value="getDeparturePlace.address"
              clear-icon="mdi-close-circle from"
              clearable
              class="cursor-pointer_input pt-0"
              :hide-details="(getIsOpsZoneShouldBePrefilled && !prefilledOpsZoneId) ? false : !errors.length"
              :error="(getIsOpsZoneShouldBePrefilled && !prefilledOpsZoneId) ? null : !!errors.length"
              :error-messages="(getIsOpsZoneShouldBePrefilled && !prefilledOpsZoneId) ? null : $t(errors[0])"
              :label="getDeparturePlace.address ? '' : $t('bookingForm.from')"
              @click:clear="clearDeparturePlaceField" 
          >
          <template #prepend>
            <VIcon v-if="getDeparturePlaceIcon && getDeparturePlaceIcon.includes('mdi')">
              {{ getDeparturePlaceIcon }}
            </VIcon>
            <div
              v-else-if="getDeparturePlaceIcon"
              class="img-wrapper"
            >
              <img
                :src="require(`@slg/web-customer-shared/src/assets/img/${getDeparturePlaceIcon}.svg`)"
                :alt="$t('imageAlt.pickupPlaceType')"
              >
            </div>
          </template>
          </VTextField>
        </ValidationProvider>
      </div>
      <div @click.stop="openDropOffDialog">
        <ValidationProvider
            tag="div"
            v-slot="{ errors, ariaInput }"
            ref="arrival"
            name="arrival"
            mode="lazy"
            :rules="{ required: true }"
        >
          <VTextField
              v-bind="ariaInput"
              readonly
              :value="getArrivalPlace.address"
              class="cursor-pointer_input pt-0 pb-5"
              clear-icon="mdi-close-circle to"
              data-test="directionFieldTo"
              clearable
              :hide-details="(getIsOpsZoneShouldBePrefilled && !prefilledOpsZoneId) ? false : !errors.length"
              :error="(getIsOpsZoneShouldBePrefilled && !prefilledOpsZoneId) ? null : !!errors.length"
               :error-messages="(getIsOpsZoneShouldBePrefilled && !prefilledOpsZoneId) ? null : $t(errors[0]) "
              :label="getArrivalPlace.address ? '' : $t('bookingForm.to')"
              @click:clear="clearArrivalPlaceField" 
          >
           <template #prepend>
            <VIcon v-if="getArrivalPlaceIcon && getArrivalPlaceIcon.includes('mdi')">
              {{ getArrivalPlaceIcon }}
            </VIcon>
            <div v-else-if="getArrivalPlaceIcon">
              <img 
                :src="require(`@slg/web-customer-shared/src/assets/img/${getArrivalPlaceIcon}.svg`)"
                :alt="$t('imageAlt.dropoffPlaceType')"
              >
            </div>
          </template>
          </VTextField>
        </ValidationProvider>
      </div>
    </ValidationObserver>
  </div>
</template>

<script lang="ts">
export default {
  name: 'SDirectionSelection',
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { v4 as generateUuid } from 'uuid';
import { getIconNameForBookingFromFromMarkerType } from '@slg/web-customer-shared/src/utils/iconResolver';
import OpsZoneService from '@slg/web-customer-shared/src/services/OpsZoneService';
import SearchRequest from '@slg/web-customer-shared/src/models/search/SearchRequest';
import StringFilter from '@slg/web-customer-shared/src/models/search/StringFilter';
import BooleanFilter from '@slg/web-customer-shared/src/models/search/BooleanFilter';
import { useGlobalErrorDialog, useI18n, useRouteRef } from '../../../composables';
import DirectionDialog from './DirectionDialog.vue';
import { generatePoiList } from './sDirectionSelection.logic';
import Spinner from './Spinner.vue';

const props = defineProps({
  getArrivalPlace: { type: Object, default: () => ({}) },
  getDeparturePlace: { type: Object, default: () => ({}) },
  getIsArrivalPlaceFilled: { type: Boolean, default: false },
  getIsDeparturePlaceFilled: { type: Boolean, default: false },
  getIsOpsZoneShouldBePrefilled: { type: Boolean, default: false },
  prefilledOpsZoneId: { type: String, default: null },
  communeCoordinates: { type: Array, default: () => [] },
  isNightCardsSupport: { type: Boolean, default: false },
  additionalMapZonesColor: { type: String, default: '' },
  opsZoneColor: { type: String, default: '' },
  opsZoneMapFill: { type: String, default: '' },
  opsZoneMapStroke: { type: String, default: '' },
  communeMapFill: { type: String, default: '' },
  communeMapStroke: { type: String, default: '' },
});

const emit = defineEmits([
  'clearDeparturePlace', 'clearBookingForm', 'clearArrivalPlace',
  'trackGtmEventFromInput', 'validateBookingForm', 'trackGtmEventToInput',
  'setDeparturePlace', 'trackGtmEventSelectDeparturePlace',
  'trackGtmEventSelectArrivalPlace', 'setArrivalPlace',
  'populateOpsZone', 'setRideType',
]);

const { setGlobalError } = useGlobalErrorDialog();
const { t } = useI18n();
const route = useRouteRef();

const directionDialog = ref(null);
const error = ref('');
const opsZoneList = ref([]);
const isLoading = ref(false);
const isLoadingSelectedOpsZone = ref(false);
const currentStep = ref('');
const stepsMeta = { pickUp: 'pickUp', dropOff: 'dropOff' };
const poiList = ref({});
const poiListWithoutGrouping = ref([]);
const isDialogOpen = ref(false);

const getDeparturePlaceIcon = computed(() => (
  props.getDeparturePlace.address &&
  getIconNameForBookingFromFromMarkerType(props.getDeparturePlace.type)
));
const getArrivalPlaceIcon = computed(() => (
  props.getArrivalPlace.address &&
  getIconNameForBookingFromFromMarkerType(props.getArrivalPlace.type)
));

watch(() => route.value.params.lang, () => {
  poiList.value = generatePoiList();
});
poiList.value = generatePoiList();

function clearDeparturePlaceField() {
  emit('clearDeparturePlace');
  emit('clearBookingForm');
}
function clearArrivalPlaceField() {
  emit('clearArrivalPlace');
  emit('clearBookingForm');
}
function openPickUpDialog() {
  emit('trackGtmEventFromInput');
  if (props.getIsOpsZoneShouldBePrefilled && !props.prefilledOpsZoneId) {
    emit('validateBookingForm');
  } else {
    isDialogOpen.value = true;
    currentStep.value = stepsMeta.pickUp;
  }
}
function openDropOffDialog() {
  emit('trackGtmEventToInput');
  if (props.getIsOpsZoneShouldBePrefilled && !props.prefilledOpsZoneId) {
    emit('validateBookingForm');
  } else {
    isDialogOpen.value = true;
    currentStep.value = stepsMeta.dropOff;
  }
}
function closeDialog() {
  isDialogOpen.value = false;
}
async function invalidateMapSize() {
  await setTimeout(() => {
    directionDialog.value.invalidateMapSize();
  });
}
async function getOpsZoneIdsByStopList(stops) {
  let res;
  try {
    res = await OpsZoneService.opsZoneRequests({ stops });
  } catch (err) {
    await setGlobalError(err);
  }
  const { data } = res;
  return data;
}
async function searchOpsZones(summarizedOpsZoneList = [], offset = 0) {
  const searchRequest = new SearchRequest();
  if (props.getIsOpsZoneShouldBePrefilled) {
    const prefilledOpsZoneIdFilter = new StringFilter('opsZoneId', [props.prefilledOpsZoneId], true);
    searchRequest.addStrFilters([prefilledOpsZoneIdFilter]);
  }
  searchRequest.offset = offset;
  const { data } = await OpsZoneService.opsZoneSearch(searchRequest);
  const { opsZones, totalCount } = data;
  summarizedOpsZoneList = [...summarizedOpsZoneList, ...opsZones];
  offset += 50;
  if (offset < totalCount) {
    searchOpsZones(summarizedOpsZoneList, offset);
  } else {
    opsZoneList.value = summarizedOpsZoneList;
  }
}
async function setOpsZonesByStops(opsZoneIdsList = []) {
  if (
    (!props.getIsArrivalPlaceFilled && !props.getIsDeparturePlaceFilled) ||
    (currentStep.value === stepsMeta.pickUp && !props.getIsArrivalPlaceFilled) ||
    (currentStep.value === stepsMeta.dropOff && !props.getIsDeparturePlaceFilled)
  ) {
    try {
      await searchOpsZones([]);
    } catch (err) {
      await setGlobalError(err);
    }
    return;
  }
  const currentPlace = currentStep.value === stepsMeta.pickUp ? props.getArrivalPlace : props.getDeparturePlace;
  const stop = {
    latitude: currentPlace.lat,
    longitude: currentPlace.lon,
    poiStopId: currentPlace.isPoi && currentPlace.poiStopId,
    stopAddress: currentPlace.address,
    stopId: generateUuid(),
  };
  if (!currentPlace.isPoi) {
    delete stop.poiStopId;
  }
  const { opsZoneIds } = opsZoneIdsList.length
    ? { opsZoneIds: opsZoneIdsList }
    : await getOpsZoneIdsByStopList([stop]);
  const searchRequest = new SearchRequest();
  const opsZoneIdsFilter = new StringFilter('opsZoneId', opsZoneIds, true);
  searchRequest.addStrFilters([opsZoneIdsFilter]);
  const { data } = await OpsZoneService.opsZoneSearch(searchRequest);
  opsZoneList.value = data.opsZones;
}
function clearPoiList() {
  Object.keys(poiList.value).forEach((key) => {
    poiList.value[key].items = [];
  });
}
function populatePoiList(data) {
  clearPoiList();
  poiListWithoutGrouping.value = data;
  const { poiStopId } = currentStep.value === stepsMeta.dropOff ? props.getDeparturePlace : props.getArrivalPlace;
  data.forEach((item) => {
    if (item.stops.every((stop) => stop.poiStopId !== poiStopId)) {
      const stops = item.stops.reduce((accumulator, currentValue) => {
        const stop = [{
          name: currentValue.name,
          address: currentValue.address,
          lat: currentValue.latitude,
          lon: currentValue.longitude,
          description: currentValue.description,
          id: currentValue.poiStopId,
          parentInd: item.pointOfInterestId,
          type: item.type,
        }];
        accumulator = accumulator.concat(stop);
        return accumulator;
      }, []);
      poiList.value[item.type]?.items?.push({
        poiId: item.pointOfInterestId,
        name: item.name,
        address: item.address,
        deep: item.stops.length > 1,
        subItems: stops,
      });
    }
  });
}
async function searchPoi(poiIdsList, summarizedPoiList = []) {
  const searchRequest = new SearchRequest();
  const poiIdsFilter = new StringFilter('pointOfInterestId', poiIdsList, true);
  const poiActiveStateFilter = new BooleanFilter('disabled', false);
  searchRequest.addStrFilters([poiIdsFilter]);
  searchRequest.addBoolFilters([poiActiveStateFilter]);
  try {
    const { data } = await OpsZoneService.poiListSearch(searchRequest);
    const { pointsOfInterest, totalCount } = data;
    summarizedPoiList = [...summarizedPoiList, ...pointsOfInterest];
    if (pointsOfInterest.length < totalCount) {
      const foundPoiIds = pointsOfInterest.map(({ pointOfInterestId }) => pointOfInterestId);
      const restIds = poiIdsList.filter((poiId) => !foundPoiIds.find((foundPoiId) => foundPoiId === poiId));
      await searchPoi(restIds, summarizedPoiList);
    } else {
      populatePoiList(summarizedPoiList);
    }
  } catch (err) {
    await setGlobalError(err);
  }
}
async function setPoiListByZoneListIds() {
  const poiIdsList = opsZoneList.value.map(({ pointOfInterestIds }) => pointOfInterestIds).flat(Infinity);
  if (opsZoneList.value.length && !poiIdsList.length) {
    clearPoiList();
    return;
  }
  await searchPoi(poiIdsList, []);
}
async function loadState() {
  isLoading.value = true;
  try {
    await setOpsZonesByStops();
    await setPoiListByZoneListIds();
  } catch (err) {
    await setGlobalError(err);
  }
  isLoading.value = false;
}
async function changeStep({ step, opsZoneIdsList }) {
  if (currentStep.value === step) {
    return;
  }
  error.value = '';
  isLoading.value = true;
  currentStep.value = step;
  try {
    await setOpsZonesByStops(opsZoneIdsList);
    await setPoiListByZoneListIds();
  } catch (err) {
    await setGlobalError(err);
  }
  isLoading.value = false;
}
function setError(err) {
  error.value = err;
}
async function setLocation(location) {
  isLoading.value = !props.getIsDeparturePlaceFilled && !props.getIsArrivalPlaceFilled;
  isLoadingSelectedOpsZone.value = props.getIsDeparturePlaceFilled || props.getIsArrivalPlaceFilled;
  const stop = {
    latitude: location.lat,
    longitude: location.lon,
    poiStopId: location.isPoi && location.poiStopId,
    stopAddress: location.address,
    stopId: generateUuid(),
  };
  if (!location.isPoi) {
    delete stop.poiStopId;
    delete stop.name;
  }
  const data = await getOpsZoneIdsByStopList([stop]);
  if (data.errorCode) {
    const stopType = currentStep.value === stepsMeta.pickUp ? t('bookingForm.pickUpAddress') : t('bookingForm.dropOffAddress');
    error.value = t(`bookingForm.errors.${data.errorCode}`, { stopType });
    isLoading.value = false;
    return;
  }
  const { opsZoneIds } = data;
  let selectedPoiStopName;
  let selectedPoiName;
  if (location.isPoi && location.poiStopId) {
    const selectedPoi = poiListWithoutGrouping.value.find((poi) =>
      poi.stops.find(({ poiStopId }) => poiStopId === location.poiStopId)
    );
    selectedPoiName = selectedPoi?.name;
    const poiStopsList = poiListWithoutGrouping.value.map((poi) => poi.stops);
    const selectedPoiStop = poiStopsList.flat().find((poiStop) => location.poiStopId === poiStop.poiStopId);
    selectedPoiStopName = selectedPoiStop.name;
  }
  const dataToSet = { ...location, poiStopName: selectedPoiStopName, poiName: selectedPoiName, stopId: stop.stopId };
  if (currentStep.value === stepsMeta.pickUp) {
    await emit('setDeparturePlace', dataToSet);
    emit('trackGtmEventSelectDeparturePlace', dataToSet);
  } else {
    emit('trackGtmEventSelectArrivalPlace', dataToSet);
    await emit('setArrivalPlace', dataToSet);
  }
  if (props.getIsArrivalPlaceFilled && props.getIsDeparturePlaceFilled) {
    const selectedStops = [props.getDeparturePlace, props.getArrivalPlace].map((selectedStop) => ({
      latitude: selectedStop.lat,
      longitude: selectedStop.lon,
      poiStopId: selectedStop.poiStopId || '',
      stopAddress: selectedStop.address,
      stopId: selectedStop.stopId,
    }));
    const requestData = await getOpsZoneIdsByStopList(selectedStops);
    if (requestData.errorCode) {
      if (requestData.errorCode === 'OPS_ZONES_SOME_STOPS_IN_RESTRICTED_AREA') {
        const invalidStopId = requestData.restrictedAreaStops[0].stopId;
        const selectedStopsList = [props.getDeparturePlace, props.getArrivalPlace];
        const invalidStop = selectedStopsList.find((selectedStop) => selectedStop.stopId === invalidStopId);
        const valitStop = selectedStopsList.find((selectedStop) => {
          if (selectedStop.stopId !== invalidStopId) {
            return {
              latitude: selectedStop.lat,
              longitude: selectedStop.lon,
              poiStopId: selectedStop.isPoi && location.poiStopId,
              stopAddress: selectedStop.address,
              stopId: selectedStop.stopId,
            };
          }
        });
        const ivalidStep = props.getDeparturePlace.stopId === invalidStop.stopId ? stepsMeta.pickUp : stepsMeta.dropOff;
        const zoneList = await getOpsZoneIdsByStopList([valitStop]);
        await changeStep({ step: ivalidStep, opsZoneIdsList: zoneList.opsZoneIds });
        if (currentStep.value === stepsMeta.pickUp) {
          emit('clearDeparturePlace');
        } else {
          emit('clearArrivalPlace');
        }
        const stopType = currentStep.value === stepsMeta.pickUp ? t('bookingForm.pickUpAddress') : t('bookingForm.dropOffAddress');
        error.value = t(`bookingForm.errors.${requestData.errorCode}`, { stopType });
      } else {
        const stopType = currentStep.value === stepsMeta.pickUp ? t('bookingForm.pickUpAddress') : t('bookingForm.dropOffAddress');
        if (currentStep.value === stepsMeta.pickUp) {
          emit('clearDeparturePlace');
        } else {
          emit('clearArrivalPlace');
        }
        error.value = t(`bookingForm.errors.${requestData.errorCode}`, { stopType });
      }
      isLoading.value = false;
      return;
    } else {
      const { opsZoneIds: selectedOpsZoneIds } = requestData;
      const selectedZoneId = selectedOpsZoneIds[0];
      const searchRequest = new SearchRequest();
      const opsZoneIdsFilter = new StringFilter('opsZoneId', [selectedZoneId], true);
      searchRequest.addStrFilters([opsZoneIdsFilter]);
      try {
        const { data: opsData } = await OpsZoneService.opsZoneSearch(searchRequest);
        const { opsZones } = opsData;
        emit('populateOpsZone', opsZones[0]);
        emit('setRideType', opsZones[0].shuttleType);
        isLoadingSelectedOpsZone.value = false;
        closeDialog();
        return;
      } catch (err) {
        await setGlobalError(err);
      }
    }
  }
  if (props.getIsDeparturePlaceFilled) {
    await changeStep({ step: stepsMeta.dropOff, opsZoneIds });
  }
  if (props.getIsArrivalPlaceFilled) {
    await changeStep({ step: stepsMeta.pickUp, opsZoneIds });
  }
  isLoadingSelectedOpsZone.value = false;
  isLoading.value = false;
}
</script>

<style lang="scss">
.direction-dialog {
  min-height: 381px;
  width: 100%;
  margin-right: 8px;
  margin-left: 8px;
  overflow-y: auto;

  .v-input__prepend-outer {
    width: 24px;
    height: 24px;

    img {
      width: 100%;
      height: 100%;
    }
  }
}
</style>
